import {
    useState,
    useEffect,
    useMemo,
    useCallback,
} from 'react';
import CopyButton from 'components/UI/CopyButton';
import {
    webLibSdk,
    formatPrice,
    getCurrentTimeZoneDate,
    getFormatterAllocationType,
} from 'helpers';
import { CUSTOM_ALLOCATION_TYPE } from '_constants/allocationTypes';
import CancelAllocation from './CancelAllocation';
import CancelationTimer from './CancelationTimer';

const useAllocatedAddressesTypes = (types, initialTypes, filterDate, addressSearch, distributionAllocationType) => {
    const [addresses, setAddresses] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(1);
    const [perPage, setPerPage] = useState(20);

    const isCustom = useMemo(() => distributionAllocationType === CUSTOM_ALLOCATION_TYPE, [distributionAllocationType]);

    const getData = useCallback(async (currPage = 1) => {
        setLoading(true);
        const currPageAddresses = addresses.slice((currPage - 1) * perPage, currPage * perPage);
        const newData = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const address of currPageAddresses) {
            // eslint-disable-next-line no-await-in-loop
            const allocationInfo = await (isCustom ? webLibSdk.customAllocation(address) : webLibSdk.allocation(address));
            newData.push({ address, ...allocationInfo });
        }
        newData.sort((a, b) => (a.allocationTime <= b.allocationTime ? 1 : -1));
        setData(newData);
        setLoading(false);
    }, [addresses, perPage, isCustom]);

    const filteredData = useMemo(() => {
        const filterConditions = {
            dateFilter: false,
            addressSearch: false,
        };

        if (
            filterDate
            && filterDate[0]
            && filterDate[1]
            && new Date(filterDate[0]) !== 'Invalid Date'
            && new Date(filterDate[1]) !== 'Invalid Date'
        ) filterConditions.dateFilter = true;

        if (addressSearch) filterConditions.addressSearch = true;

        if (!Object.values(filterConditions).find((x) => x)) return data;

        return data.filter(({ address, allocationTime }) => {
            const allocTimeDate = new Date((allocationTime));
            return (!filterConditions.dateFilter || (allocTimeDate > new Date(filterDate[0]) && allocTimeDate < new Date(filterDate[1]))) && (!addressSearch || address.toLowerCase().includes(addressSearch.toLowerCase()));
        });
    }, [data, filterDate, addressSearch]);

    const columns = useMemo(() => [
        {
            name: 'Address',
            selector: (row) => (
                <div className="address-column">
                    <a
                        href={webLibSdk.getTransactionUrl(row.address, true)}
                        target="_blank"
                        rel="noreferrer">
                        {row.address}
                    </a>
                    <CopyButton
                        key={`${row.address}-copy-address`}
                        noText
                        text={row.address}
                    />
                </div>
            ),
            width: '380px',
            center: true,
        },
        {
            name: 'Type',
            selector: (row) => (<p className="allocatedType">{getFormatterAllocationType(isCustom ? 'Custom Sale' : row.allocationType)}</p>),
            center: true,
            width: '150px',
        },
        {
            name: 'Lockup Period',
            selector: (row) => {
                const lockupPeriod = isCustom ? row?.lockupPeriod : (initialTypes.find((x) => x.type === row.allocationType)?.lockupPeriod || 0);
                const months = Math.floor(lockupPeriod / (3600 * 24 * 30));

                return (
                    <p className="allocatedLockup">
                        {`${months || '-----'} ${months ? 'Months' : ''}`}
                    </p>
                );
            },
            center: true,
            width: '120px',
        },
        {
            name: 'Amount',
            selector: (row) => formatPrice(row.amount),
            sortable: true,
            center: true,
            width: '110px',
            sortFunction: (rowA, rowB) => {
                const a = Number(rowA.amount || 0);
                const b = Number(rowB.amount || 0);

                return a > b ? 1 : -1;
            },
        },
        {
            name: 'Status',
            selector: (row) => (<p className="allocatedStatus">{row.state}</p>),
            center: true,
        },
        {
            name: 'Date',
            selector: (row) => getCurrentTimeZoneDate(row.allocationTime),
            sortable: true,
            center: true,
            width: '170px',
        },
        {
            name: 'Actions',
            selector: (row) => {
                const hasAccess = isCustom ? types.type === 'Admin' : types.find((x) => x.type === row.allocationType);
                return (
                    <CancelAllocation
                        key={`${row.address}-${isCustom ? 'custom' : 'standard'}-cancel-button`}
                        {...row}
                        hasAccess={hasAccess}
                        isCustom={isCustom}
                    />
                );
            },
            width: '160px',
            center: true,
        },
        {
            name: 'Cancel. Time',
            selector: (row) => {
                const hasAccess = isCustom ? types.type === 'Admin' : types.find((x) => x.type === row.allocationType);
                return (
                    <CancelationTimer
                        {...row}
                        key={`${row.address}-${isCustom ? 'custom' : 'standard'}-cancelation-time`}
                        hasAccess={hasAccess}
                        isCustom={isCustom}
                    />
                );
            },
            center: true,
        },
    ], [isCustom, types]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const getAddresses = await webLibSdk[isCustom ? 'customAllocatedAddresses' : 'allocatedAddresses']();
            setAddresses([...getAddresses].reverse());
            setTotalRows(getAddresses.length);
            if (!getAddresses.length) setLoading(false);
        };

        init();
    }, [isCustom]);

    const handlePerRowsChange = useCallback((newPerPage, currPage) => {
        getData(currPage);
        setPerPage(newPerPage);
    }, []);

    useEffect(() => {
        if (!addresses.length) return;

        getData();
    }, [addresses, getData]);

    return {
        loading,
        totalRows,
        filteredData,
        columns,
        perPage,
        handlePerRowsChange,
        getData,
    };
};

export default useAllocatedAddressesTypes;
